import React from 'react';
import styled from 'styled-components';
import { GatsbyImage } from "gatsby-plugin-image";

import { Overline } from '../layouts/Section';
import { Grid, Cell } from '../layouts/Grid';
import Text from '../Text';
import HTMLWrapper from '../HTMLWrapper';
import BasicButton from '../BasicButton';
import StatBlock from '../StatBlock';
import BasicTestimonial from "@basic/BasicTestimonial";
import Video from "../WistiaVideo";

import documentToReactComponents from '../../util/documentToReactComponents';

import theme from '@theme';

const { breakPoints: { screenQuery } } = theme;

const ImageBlockGrid = styled(Grid)`
    .image-block__button {
        margin-right: 28px;
    }
`

const SpacingCell = styled(Cell)`
	img {
		width: 100%;
	}
	
	${screenQuery.small} {
		padding-bottom: 30px;
	}
`;

const AdditionalInfoSection = styled.div`
    background-color: ${(props) => props.box && "rgba(120, 158, 232, 0.07)"};
    margin-top: 20px;
    margin-bottom: 20px;
`

const ImageBlockStatBlock = styled(StatBlock)`
    padding: 30px;

`

const ImageBlockTestimonial = styled(BasicTestimonial)`
    padding: 40px;

    .main-quote {
        font-size: 17px;
        font-style: italic;
        line-height: 28px; /* 164.706% */
        letter-spacing: 0.17px;
        margin-bottom: 24px;
    }

    .bio-name {
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 2.6px;
        text-transform: uppercase;
    }

    .bio-title {
        font-size: 14px;
        font-weight: 600;
        letter-spacing: 0.14px;
    }

    .home-testimonial-bio img {
        max-width: 75px;
        border-radius: 50%;
    }
`;
 
const ImageBlock = ({
    image,
    videoLink,
    videoThumbnail,
    className,
    stackedBlock,
    overline,
    header,
    subheader,
    additionalSubheader,
    buttons,
    orangeButton,
    spanA,
    spanAStart,
    spanB,
    spanBStart,
    flipX,
    boxStyle,
    lineStyle,
    
}) => {
    return (
        
        <ImageBlockGrid className={`single-image-grid ${className}`} noSmallGap>
            {image && (
				<SpacingCell
					css={{ height: "100%", width: "100%" }}
					span={spanA || 6}
                    start={spanAStart || (flipX ? 7 : 1)}
					rowStartSm={1}
					spanSm={8}
					startSm={3}
					className="vertical-center image-block_img-container"
				>
                    {image.gatsbyImageData ? (
						<GatsbyImage  
                            className="image-block_img" 
                            image={image.gatsbyImageData} 
                            data-testid='gatsby-image'
                        />
					) : ( 
                        <img 
                            className="image-block_img" 
                            data-testid='standard-image'
                            src={image?.file?.url || image} 
                        /> 
                    )}
				</SpacingCell>
			)}
            {videoLink && (
                <SpacingCell
                    css={{ height: "100%", width: "100%" }}
                    span={spanA || 6}
                    start={spanAStart || (flipX ? 7 : 1)}
                    rowStartSm={1}
                    spanSm={8}
                    startSm={3}
                    className="vertical-center image-block_img-container"
                >
                    <Video
                        videoId={videoLink.slice(
                            videoLink.lastIndexOf("/") + 1,
                            videoLink.length
                        )}
                        thumbnail={videoThumbnail ? videoThumbnail?.file?.url : ''}
                    />
                </SpacingCell>
            )}
            <Cell
                align="center"
				span={spanB || 6}
				start={spanBStart || (flipX ? 1 : 7)}
				rowStart={flipX ? 1 : null}
                rowStartSm={2}
                className="vertical-center image-block_text-container"
            >
                {overline && (
					<Overline component="caps" className="image-block-overline">
						{overline}
					</Overline>
				)}
			    {header && <Text component={stackedBlock ? "h3" : "h2"} className="image-block__header">{header}</Text>}
                {subheader && (
                    typeof subheader === 'object' ? (
                        <Text component="b4" className="image-block__subheader">
							<HTMLWrapper className="image-block__subheader">{documentToReactComponents(subheader)}</HTMLWrapper>
                        </Text>
                    ) : (
                        <Text component="b4" className="image-block__subheader">
                            <HTMLWrapper oneColumnList html={subheader} />
                        </Text>
                    )
                )}
                {buttons?.map(button => {
                    return (
                        <BasicButton
                            key={button.contentful_id}
                            className="image-block__button"
                            style={{ marginTop: "10px" }}
                            arrow={!orangeButton}
                            secondary={!orangeButton}
                            to={button.linkUrl}
                            data-testid="image-block__button"
                        >
                            {button.linkText}
                        </BasicButton>
                    )
                })}
                <AdditionalInfoSection box={boxStyle} line={lineStyle}>
                    {additionalSubheader?.map((reference) => {
                        if (reference.type === "ContentfulStatBlock") {
                            return (
                                <ImageBlockStatBlock
                                    individualStat={reference.statistic}
                                    individualStatDescription={
                                        reference.statisticDescription.childMarkdownRemark.html
                                    }
                                    logo={reference?.statisticIcon?.file.url}
                                    data-testid="image-block__stat-block"
                                />
                            );
                        }

                        if (reference.type === "ContentfulPhotoQuote") {
                            return (
                                <ImageBlockTestimonial
                                    quote={reference.quote.quote}
                                    bioPhoto={reference.quoteSourceBioPhoto?.file.url}
                                    bioName={reference.quoteSource}
                                    bioTitle={reference.quoteSourceJobTitle}
                                    inlineQuotes
                                    data-testid="image-block__testimonial"
                                />
                            )
                        }
                    })}
                    </AdditionalInfoSection>
            </Cell>
        </ImageBlockGrid>
    )
}
 
export default ImageBlock